import { ServiceInfoDc } from "@evergis/api";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDialogShadow, Wizard } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { DialogTitle } from "../../../DialogComponents/DialogTitle";
import { FullScreenDialog } from "../../../DialogComponents/FullScreenDialog";
import { CopySwitch } from "./CopySwitch";
import { CopyToExisting } from "./CopyToExisting/CopyToExisting";
import { CopyToNew } from "./CopyToNew";
import { WizardWrapper } from "./styled";
import { RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { LinearProgress, LoadingDescription } from "../../../BulkCopy/styled";

import { dialogResourceSelector } from "../../../../../ducks/dialogs/selectors";
import { layerInfosWithDeps, layers } from "../../../../../resources/layers";
import { useProjectLayerInfos } from "../../../../../hooks/project/useProjectLayers";
import { useInputResource } from "../../../../../modules/Catalog/hooks/useInputResource";
import { useLayerDetails } from "./hooks/useLayerDetails";
import { styledHeaderText } from "../../../styled";

import { LayerInfo } from "../../../../../interfaces/resourceInfo";

// eslint-disable-next-line max-lines-per-function
export const LayerCopy: FC = () => {
  const { t } = useTranslation();
  const layerToCopy: string = useSelector(dialogResourceSelector);
  const { data: layerInfo, loading, refetch } = layerInfosWithDeps.use({ input: [layerToCopy], waitInput: true });
  const { shadows, setScrollContainer } = useDialogShadow();
  const [isCopyToNew, setCopyToNew] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { geometryType: sourceGeometry, name: sourceName } = useLayerDetails(layerInfo?.layers[0] as LayerInfo);
  const { layerInfos: projectLayers, loading: projectLayersLoading } = useProjectLayerInfos();
  const input = useInputResource();
  const { data: { items: allLayers } = {}, loading: allLayersLoading } = layers.use({ input, waitInput: true });
  const [filteredLayers, setFilteredLayers] = useState<ServiceInfoDc[] | undefined>();
  const [isProxyLayer, setIsProxyLayer] = useState(false);
  const [isRasterLayer, setIsRasterLayer] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: layersWithDeps } = await refetch();

      if (layersWithDeps?.layers[0].type === "ProxyService") {
        setIsProxyLayer(true);
        setCopyToNew(true);
      }

      if (layersWithDeps?.layers[0].type === "PostgresTileLayerService") {
        setIsRasterLayer(true);
        setCopyToNew(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!projectLayers || !allLayers || !sourceName || !sourceGeometry) {
      return;
    }

    setFilteredLayers(
      (projectLayers.length ? projectLayers : allLayers)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .filter(item => item.geometryType === sourceGeometry && item.name !== sourceName),
    );
  }, [projectLayers, allLayers, sourceName, sourceGeometry]);

  useEffect(() => {
    setCurrentStep(0);
  }, [isCopyToNew]);

  return (
    <FullScreenDialog
      dialogContext={{
        title: {
          text: styledHeaderText(t("copyResource.copyLayer", { ns: "dialogs" })),
          icon: "settings",
        },
        meta: {
          aliasLabel: t("copyResource.layerName", { ns: "dialogs" }),
          descriptionLabel: t("copyResource.layerDescription", { ns: "dialogs" }),
          loaderLabel: t("copyResource.uploadLayerPreview", { ns: "dialogs" }),
        },
        applyText: t("actions.copy", { ns: "common" }),
      }}
    >
      <DialogTitle
        displayShadow={shadows.upper}
        extra={
          filteredLayers?.length > 0 && (
            <>
              <CopySwitch
                onToggle={setCopyToNew}
                hidden={isProxyLayer || isRasterLayer || (!isCopyToNew && currentStep === 2)}
              />
              {!isCopyToNew && (
                <WizardWrapper>
                  <Wizard
                    currentStep={currentStep}
                    steps={[
                      {
                        label: t("copyResource.setSourceLayer", { ns: "dialogs" }),
                        disabled: currentStep === 2,
                      },
                      {
                        label: t("copyResource.setAttributesToCopy", { ns: "dialogs" }),
                        disabled: currentStep === 2,
                      },
                      {
                        label: t("copyResource.copying", { ns: "dialogs" }),
                      },
                    ]}
                  />
                </WizardWrapper>
              )}
            </>
          )
        }
      />
      {loading || projectLayersLoading || allLayersLoading || filteredLayers === undefined || !sourceName ? (
        <RestrictedWidthContainer>
          <LoadingDescription>{t("actions.loading", { ns: "common" })}...</LoadingDescription>
          <LinearProgress />
        </RestrictedWidthContainer>
      ) : (
        <>
          {!isCopyToNew && filteredLayers?.length > 0 && (
            <CopyToExisting
              layerInfo={layerInfo?.layers[0]}
              shadows={shadows}
              setScrollContainer={setScrollContainer}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              projectLayersLoading={projectLayersLoading}
              allLayersLoading={allLayersLoading}
              filteredLayers={filteredLayers || []}
            />
          )}
          {(isCopyToNew || filteredLayers?.length === 0) && (
            <CopyToNew
              layerInfo={layerInfo}
              loading={loading}
              shadows={shadows}
              setScrollContainer={setScrollContainer}
            />
          )}
        </>
      )}
    </FullScreenDialog>
  );
};
